import React, { useState } from 'react';
import {
  X,
  Package,
  Truck,
  Calendar,
  User,
  MapPin,
  FileText,
  Phone,
  Mail,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  Clock,
  AlertCircle
} from 'lucide-react';
import {
  POrderHistoryDto,
  calculateOrderCompletionPercentage
} from '../../../../types/purchaseOrderHistory';
import { purchaseOrderHistoryService } from '../../../../services/purchaseOrderHistoryService';

interface OrderDetailsModalProps {
  order: POrderHistoryDto;
  isOpen: boolean;
  onClose: () => void;
}

export const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  order,
  isOpen,
  onClose
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['header', 'lines'])
  );

  if (!isOpen) return null;

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const completionPercentage = calculateOrderCompletionPercentage(
    order.totalQuantity,
    order.totalDelivered
  );

  const isCompleted = purchaseOrderHistoryService.isOrderCompleted(order);

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="absolute inset-y-0 right-0 max-w-4xl w-full bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="bg-blue-600 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Package size={28} />
            <div>
              <h2 className="text-xl font-bold">
                {order.orderNoERP || `Ordre #${order.orderNo}`}
              </h2>
              <p className="text-blue-100 text-sm">
                Detaljert ordreinformasjon
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-blue-200 transition-colors"
            aria-label="Lukk"
          >
            <X size={28} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Status and Progress */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <span
                className={`px-3 py-1 text-sm font-medium rounded-full ${purchaseOrderHistoryService.getStatusBadgeClass(
                  order.orderStatus
                )}`}
              >
                {order.orderStatusText}
              </span>
              <div className="flex items-center space-x-2">
                {isCompleted ? (
                  <>
                    <CheckCircle className="text-green-600" size={20} />
                    <span className="text-green-700 font-medium">Fullført</span>
                  </>
                ) : (
                  <>
                    <Clock className="text-yellow-600" size={20} />
                    <span className="text-yellow-700 font-medium">Pågående</span>
                  </>
                )}
              </div>
            </div>

            <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
              <div
                className={`h-3 rounded-full transition-all ${
                  completionPercentage === 100 ? 'bg-green-500' : 'bg-blue-500'
                }`}
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
            <p className="text-sm text-gray-700 text-center">
              {order.totalDelivered} av {order.totalQuantity} enheter levert ({completionPercentage}%)
            </p>
          </div>

          {/* Order Header Section */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('header')}
              className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
            >
              <h3 className="font-semibold text-gray-900 flex items-center">
                <FileText className="mr-2" size={18} />
                Ordredetaljer
              </h3>
              {expandedSections.has('header') ? (
                <ChevronDown size={20} />
              ) : (
                <ChevronRight size={20} />
              )}
            </button>

            {expandedSections.has('header') && (
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Ordrenummer (WMS)</p>
                  <p className="font-medium text-gray-900">{order.orderNo}</p>
                </div>

                {order.orderNoERP && (
                  <div>
                    <p className="text-gray-600">Ordrenummer (ERP)</p>
                    <p className="font-medium text-gray-900">{order.orderNoERP}</p>
                  </div>
                )}

                {order.orderType && (
                  <div>
                    <p className="text-gray-600">Ordretype</p>
                    <p className="font-medium text-gray-900">{order.orderType}</p>
                  </div>
                )}

                {order.orderDate && (
                  <div>
                    <p className="text-gray-600">Ordredato</p>
                    <p className="font-medium text-gray-900">
                      {purchaseOrderHistoryService.formatDate(order.orderDate)}
                    </p>
                  </div>
                )}

                {order.deliveryDate && (
                  <div>
                    <p className="text-gray-600">Leveringsdato</p>
                    <p className="font-medium text-gray-900">
                      {purchaseOrderHistoryService.formatDate(order.deliveryDate)}
                    </p>
                  </div>
                )}

                {order.warehouse && (
                  <div>
                    <p className="text-gray-600">Lager</p>
                    <p className="font-medium text-gray-900">{order.warehouse}</p>
                  </div>
                )}

                {order.supplierOrderNo && (
                  <div>
                    <p className="text-gray-600">Leverandørens ordrenr</p>
                    <p className="font-medium text-gray-900">{order.supplierOrderNo}</p>
                  </div>
                )}

                {order.yourRef && (
                  <div>
                    <p className="text-gray-600">Deres referanse</p>
                    <p className="font-medium text-gray-900">{order.yourRef}</p>
                  </div>
                )}

                {order.ourRef && (
                  <div>
                    <p className="text-gray-600">Vår referanse</p>
                    <p className="font-medium text-gray-900">{order.ourRef}</p>
                  </div>
                )}

                {order.deliveryMethod && (
                  <div>
                    <p className="text-gray-600">Leveringsmåte</p>
                    <p className="font-medium text-gray-900">{order.deliveryMethod}</p>
                  </div>
                )}

                {order.carrierName && (
                  <div>
                    <p className="text-gray-600">Transportør</p>
                    <p className="font-medium text-gray-900">{order.carrierName}</p>
                  </div>
                )}

                {order.contactName && (
                  <div>
                    <p className="text-gray-600">Kontaktperson</p>
                    <p className="font-medium text-gray-900">{order.contactName}</p>
                  </div>
                )}

                {order.created && (
                  <div>
                    <p className="text-gray-600">Opprettet</p>
                    <p className="font-medium text-gray-900">
                      {purchaseOrderHistoryService.formatDateTime(order.created)}
                    </p>
                  </div>
                )}

                {order.createdBy && (
                  <div>
                    <p className="text-gray-600">Opprettet av</p>
                    <p className="font-medium text-gray-900">{order.createdBy}</p>
                  </div>
                )}

                {order.lastUpdate && (
                  <div>
                    <p className="text-gray-600">Sist oppdatert</p>
                    <p className="font-medium text-gray-900">
                      {purchaseOrderHistoryService.formatDateTime(order.lastUpdate)}
                    </p>
                  </div>
                )}

                {order.updatedBy && (
                  <div>
                    <p className="text-gray-600">Oppdatert av</p>
                    <p className="font-medium text-gray-900">{order.updatedBy}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Supplier Section */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('supplier')}
              className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
            >
              <h3 className="font-semibold text-gray-900 flex items-center">
                <Truck className="mr-2" size={18} />
                Leverandør
              </h3>
              {expandedSections.has('supplier') ? (
                <ChevronDown size={20} />
              ) : (
                <ChevronRight size={20} />
              )}
            </button>

            {expandedSections.has('supplier') && (
              <div className="p-4 space-y-3">
                <div>
                  <p className="text-gray-600 text-sm">Leverandørnummer</p>
                  <p className="font-medium text-gray-900">{order.supplierNo}</p>
                </div>
                {order.supplierName && (
                  <div>
                    <p className="text-gray-600 text-sm">Navn</p>
                    <p className="font-medium text-gray-900">{order.supplierName}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Summary Statistics */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('stats')}
              className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
            >
              <h3 className="font-semibold text-gray-900 flex items-center">
                <Package className="mr-2" size={18} />
                Sammendrag
              </h3>
              {expandedSections.has('stats') ? (
                <ChevronDown size={20} />
              ) : (
                <ChevronRight size={20} />
              )}
            </button>

            {expandedSections.has('stats') && (
              <div className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{order.totalLines}</p>
                    <p className="text-sm text-gray-600">Linjer</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{order.totalQuantity}</p>
                    <p className="text-sm text-gray-600">Totalt antall</p>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{order.totalDelivered}</p>
                    <p className="text-sm text-gray-600">Levert</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <p className="text-2xl font-bold text-orange-600">{order.totalTransactions}</p>
                    <p className="text-sm text-gray-600">Transaksjoner</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Order Lines Section */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('lines')}
              className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
            >
              <h3 className="font-semibold text-gray-900 flex items-center">
                <Package className="mr-2" size={18} />
                Ordrelinjer ({order.lines.length})
              </h3>
              {expandedSections.has('lines') ? (
                <ChevronDown size={20} />
              ) : (
                <ChevronRight size={20} />
              )}
            </button>

            {expandedSections.has('lines') && (
              <div className="divide-y divide-gray-200">
                {order.lines.map((line) => (
                  <div key={line.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="font-semibold text-gray-900">
                            Linje {line.lineNo}
                          </span>
                          <span
                            className={`px-2 py-0.5 text-xs font-medium rounded-full ${purchaseOrderHistoryService.getStatusBadgeClass(
                              line.lineStatus
                            )}`}
                          >
                            {line.lineStatusText}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-gray-900">{line.itemNo}</p>
                        {line.description1 && (
                          <p className="text-sm text-gray-600">{line.description1}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-gray-900">
                          {line.quantityDelivered || 0} / {line.quantity || 0}
                        </p>
                        {line.itemUnitCode && (
                          <p className="text-xs text-gray-600">{line.itemUnitCode}</p>
                        )}
                      </div>
                    </div>

                    {/* Line details */}
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mt-2">
                      {line.stdLocationName && (
                        <div className="flex items-center">
                          <MapPin className="mr-1" size={12} />
                          {line.stdLocationName}
                        </div>
                      )}
                      {line.itemBatchNo && (
                        <div>
                          Batch: {line.itemBatchNo}
                        </div>
                      )}
                      {line.transactions && (
                        <div>
                          {line.transactions.length} transaksjon{line.transactions.length !== 1 ? 'er' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Lukk
          </button>
        </div>
      </div>
    </div>
  );
};
